import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useAppContextProvider from "../../AppContext/useAppContextProvider";
import {
  AddAlertNotifications,
  MoveDGMember,
  SetAlertConfig,
} from "../../api/api";

type AlertNotificationsResult = {
  status: string;
  Msg?: string;
  errorCode?: string;
};

type AlertConfig = {
  desiredAlertInterval: number;
  chipTempThreshold: number;
  numberOfChipsOverThreshold: number;
};

export const usePostAlertConfig = () => {
  const queryClient = useQueryClient();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { getAccessTokenSilently } = useAuth0();

  const mutation = useMutation({
    mutationFn: ({
      desiredAlertInterval,
      chipTempThreshold,
      numberOfChipsOverThreshold,
      token,
    }: AlertConfig & {
      token: string;
    }): Promise<AlertNotificationsResult> =>
      SetAlertConfig(token, {
        desiredAlertInterval,
        chipTempThreshold,
        numberOfChipsOverThreshold,
      }),

    onSuccess: (data) => {
      if (data.status === "error") {
        setToastMessage(
          `Error code: ${data.errorCode}, Error msg: ${data.Msg}` ||
            `Error adding alert notifications`
        );
      } else {
        setToastMessage(
          "Miners have been moved successfully to the new group and site"
        );
      }
      setToastOpen(true);
    },

    onError: (error) => {
      console.error("Error moving miners:", error);
      setToastMessage("Error moving miners");
      setToastOpen(true);
    },
  });

  const mutateWithToken = useCallback(
    async (
      {
        desiredAlertInterval,
        chipTempThreshold,
        numberOfChipsOverThreshold,
      }: AlertConfig,

      options?: {
        onSuccess?: (
          data: AlertNotificationsResult
        ) => void;
        onError?: (error: any) => void;
        onSettled?: () => void;
      }
    ) => {
      const token = await getAccessTokenSilently();

      mutation.mutate(
        {
          desiredAlertInterval,
          chipTempThreshold,
          numberOfChipsOverThreshold,
          token,
        },
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
          onSettled: options?.onSettled,
        }
      );
    },
    [getAccessTokenSilently, mutation]
  );

  return { ...mutation, mutate: mutateWithToken };
};
